// A small composable wrapping MediaRecorder for voice notes.
//
// The microphone is only ever touched inside start() — never on import, never
// on mount. Capture happens in someone else's kitchen; asking for a
// permission nobody requested yet is the fastest way to make this app feel
// like it is spying rather than listening.
//
// This module deliberately does NOT mint an object URL. It used to, and that
// was the whole bug: URL.createObjectURL() is only valid for the lifetime of
// the page that made it, so a note recorded on Tuesday was a dead link on
// Wednesday. Recording hands back bytes; `lib/audio-store.js` is what makes
// them last.
//
// LOSING A TAKE IS THE WORST THING THIS FILE CAN DO. Everything below about
// takes, salvage and chunking exists for one reason: the screen can go away
// while someone is still talking — the iOS edge-swipe is always live, the
// header Back button is 40px from the top, and an incoming phone call
// backgrounds the page, which is when iOS Safari tears MediaRecorder down
// underneath us. A recorder that only survives the tidy path is a recorder
// that throws away the recording of somebody's mother.

import { ref, onBeforeUnmount } from 'vue';

// Safari (iOS and desktop) only ever shipped audio/mp4 recording; Chrome and
// Firefox only ship audio/webm. Trying webm first and falling through to mp4
// covers both without guessing the platform from the user agent string.
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

/* THE SINGLE MOST IMPORTANT NUMBER IN THIS FILE.
 *
 * MediaRecorder.start() with no argument buffers the entire recording inside
 * the recorder and hands it over exactly once, in a `dataavailable` event
 * fired asynchronously after stop(). That is fine when the person presses
 * Stop, and catastrophic when the browser takes the page away: there is no
 * later, the event never arrives, and twenty minutes of someone's voice was
 * never in our hands to begin with.
 *
 * start(CHUNK_MS) makes the recorder hand over what it has once a second, so
 * at every instant `chunks` already holds all but the last second of audio and
 * a synchronous teardown can still build a real file out of it. Concatenating
 * the fragments is exactly what the recorder would have done itself — webm
 * clusters after a header, fragmented mp4 after its ftyp/moov on Safari.
 *
 * The cost is one event per second and a handful of extra Blob references.
 * The benefit is that the floor on data loss stops being "the whole take".
 */
const CHUNK_MS = 1000;

function hasRecordingCapability() {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined'
  );
}

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }
  return MIME_CANDIDATES.find((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  }) ?? '';
}

/**
 * useRecorder() — call once per component that needs to record a voice note.
 *
 * @param {object} [options]
 * @param {(clip: {blob: Blob, mimeType: string, durationSeconds: number,
 *           reason: string}) => void} [options.onSalvage]
 *        Called with a take that ended without anybody asking — the screen was
 *        left, the app was backgrounded, iOS killed the recorder. THE CALLER
 *        MUST PERSIST IT. It is delivered exactly once, and it is delivered
 *        instead of, never as well as, a stop() result.
 *
 * recording        — true while a recording is in progress
 * elapsedSeconds    — whole seconds since start(), ticking while recording
 * supported         — whether this browser can record at all (device
 *                      capability, then re-checked after a permission
 *                      refusal or a construction failure)
 * micRefused        — the device can record but the microphone was refused or
 *                      unavailable. Distinct from `supported` being false for
 *                      want of a MediaRecorder at all, because THIS one is
 *                      worth offering to try again.
 * error             — a plain-language reason recording isn't happening,
 *                      or '' when there is nothing to report
 * start()           — asks for the microphone and begins recording. Resolves
 *                      true on success, false if it could not start (check
 *                      `error` for why).
 * stop()            — stops recording, releases the microphone, and resolves
 *                      { blob, mimeType, durationSeconds }, or null if
 *                      nothing was recording. The caller is responsible for
 *                      handing `blob` to putAudio() and for saying so out
 *                      loud if that fails: from here on, a recording nobody
 *                      saves is a recording that is gone.
 */
export function useRecorder(options = {}) {
  const onSalvage = typeof options.onSalvage === 'function' ? options.onSalvage : null;

  const recording = ref(false);
  const elapsedSeconds = ref(0);
  const supported = ref(hasRecordingCapability());
  const micRefused = ref(false);
  const error = ref('');

  let mediaRecorder = null;
  let stream = null;
  let chunks = [];
  let timer = null;
  let startedAt = 0;

  /* Which recording we are on. It moves on every start AND every end, so any
   * listener still holding an older number knows its take is over and stays
   * out of the way. It is also what makes a take impossible to deliver twice:
   * whoever harvests first moves the number, and everybody else gets null. */
  let takeId = 0;

  /* True between asking a recorder to stop and its 'stop' event arriving, so
   * the watchdog below doesn't treat our own stop as the platform's. */
  let awaitingStop = false;

  function releaseStream() {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
  }

  function clearTimer() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function tick() {
    elapsedSeconds.value = Math.floor((Date.now() - startedAt) / 1000);
  }

  /** Everything that ends a take, in one place and in an order that matters. */
  function endTake() {
    takeId += 1;
    awaitingStop = false;
    detachTeardownWatch();
    clearTimer();
    // The microphone is released LAST, after the bytes have been taken off the
    // recorder. Killing the tracks first is what used to happen on unmount,
    // and a recorder whose tracks have already ended has nothing left to flush.
    releaseStream();
    mediaRecorder = null;
    chunks = [];
    recording.value = false;
    elapsedSeconds.value = 0;
  }

  /**
   * Turn whatever has been collected into a clip and close the take.
   * Returns null if this take has already been handed to somebody.
   */
  function harvest(takeNo, mimeType) {
    if (takeNo !== takeId || !mediaRecorder) return null;

    const blob = new Blob(chunks, { type: mimeType });
    // The clock, not blob.duration: a webm from MediaRecorder carries no
    // duration in its header, so asking the file is asking a question it
    // cannot answer. Measured now rather than read from the ticking counter,
    // which floors and only updates every 250ms — a 1.9s take read "0:01",
    // and a take under a second read "0:00" against real audio.
    const durationSeconds = Math.max(
      chunks.length ? 1 : 0,
      Math.round((Date.now() - startedAt) / 1000),
    );

    endTake();

    return { blob, mimeType, durationSeconds };
  }

  function typeOf(recorder) {
    return (recorder && recorder.mimeType) || 'audio/webm';
  }

  // --- the screen going away -------------------------------------------------

  /**
   * A take is ending because the page is, not because anyone pressed Stop.
   *
   * `mode` is the honest part:
   *
   *   'async'  the page carries on living — a Vue unmount, an Ionic route
   *            change, the app moving to the background. `stop()` gets to
   *            finish, its final `dataavailable` arrives, and the FULL take is
   *            delivered. This is the ordinary case and it loses nothing.
   *
   *   'sync'   the page itself is being torn down (pagehide). Nothing
   *            asynchronous is guaranteed to run again — not a media event,
   *            not a promise callback — so waiting for `stop()` would mean
   *            waiting for something that will never come. We take the chunks
   *            already in hand, right now, in this call stack.
   *
   * WHAT 'sync' STILL LOSES, said plainly rather than papered over: up to
   * CHUNK_MS of trailing audio (the fragment the recorder had not handed over
   * yet), and — because writing to IndexedDB is itself asynchronous — possibly
   * the whole clip, if the browser kills the page before the write commits.
   * A synchronous handler cannot await an asynchronous event and this code does
   * not pretend otherwise. It starts the write as early as it possibly can and
   * that is the entire extent of what is available to it.
   */
  function salvage(mode, reason) {
    if (!recording.value || !mediaRecorder || awaitingStop) return;

    const recorder = mediaRecorder;
    const takeNo = takeId;
    const mimeType = typeOf(recorder);

    // An already-inactive recorder is iOS having killed it while we were
    // backgrounded. stop() would throw or do nothing and no event is coming,
    // so the chunks in hand are the whole of what exists.
    const nothingAsyncLeft = mode === 'sync' || recorder.state === 'inactive';

    if (nothingAsyncLeft) {
      const clip = harvest(takeNo, mimeType);
      try {
        if (recorder.state === 'recording' || recorder.state === 'paused') recorder.stop();
      } catch { /* it was already gone; the bytes are what matter */ }
      if (clip) deliver(clip, reason);
      return;
    }

    stop().then((clip) => {
      if (clip) deliver(clip, reason);
    }).catch(() => { /* stop() does not reject; belt and braces */ });
  }

  function deliver(clip, reason) {
    if (!onSalvage) return;
    try {
      onSalvage({ ...clip, reason });
    } catch { /* the caller's problem, and not worth taking the page down for */ }
  }

  function onVisibilityChange() {
    // Backgrounding is what an incoming phone call looks like from in here,
    // and iOS Safari stops MediaRecorder when it happens — without firing a
    // single Vue lifecycle hook. The page keeps executing for a short while
    // after this event, which is why 'async' is the right mode: it is enough
    // for stop() to flush and for the IndexedDB write to start.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      salvage('async', 'backgrounded');
    }
  }

  function onPageHide() {
    salvage('sync', 'page-hidden');
  }

  function attachTeardownWatch() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', onPageHide);
    }
  }

  function detachTeardownWatch() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', onPageHide);
    }
  }

  // --- recording -------------------------------------------------------------

  async function start() {
    error.value = '';

    if (!hasRecordingCapability()) {
      supported.value = false;
      micRefused.value = false;
      error.value = 'This browser cannot record audio here. Type the note instead.';
      return false;
    }

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Permission refused, no microphone present, or the device is in use
      // elsewhere — from here they all look the same and all mean the same
      // thing to the person holding the phone: recording is not available.
      //
      // It is NOT, however, the end of the conversation. A soft denial can be
      // re-prompted from the next user gesture and a hard one can be undone in
      // Settings, so this is recorded as a refusal the screen can offer to
      // try again rather than as a device that cannot record.
      supported.value = false;
      micRefused.value = true;
      error.value = 'Microphone permission was refused. Type the note instead.';
      return false;
    }

    const mimeType = pickMimeType();
    try {
      mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      error.value = 'Recording is not supported on this device. Type the note instead.';
      supported.value = false;
      micRefused.value = false;
      releaseStream();
      return false;
    }

    takeId += 1;
    const takeNo = takeId;
    const recorder = mediaRecorder;
    chunks = [];
    awaitingStop = false;

    recorder.addEventListener('dataavailable', (evt) => {
      // A fragment from a take that is already closed belongs to nobody.
      if (takeNo !== takeId) return;
      if (evt.data && evt.data.size > 0) chunks.push(evt.data);
    });

    recorder.addEventListener('error', () => {
      if (takeNo !== takeId) return;
      error.value = 'Recording stopped unexpectedly.';
    });

    /* THE WATCHDOG. A recorder that stops without being asked — the track
     * ended, the OS took the microphone for a phone call, iOS killed it on
     * backgrounding — fires 'stop' just the same. Left unhandled, that is a
     * take sitting in `chunks` that nothing will ever come back for. */
    recorder.addEventListener('stop', () => {
      if (takeNo !== takeId || awaitingStop) return;
      const clip = harvest(takeNo, typeOf(recorder));
      if (clip) deliver(clip, 'recorder-stopped');
    });

    recorder.start(CHUNK_MS);
    startedAt = Date.now();
    elapsedSeconds.value = 0;
    clearTimer();
    timer = setInterval(tick, 250);
    supported.value = true;
    micRefused.value = false;
    recording.value = true;
    attachTeardownWatch();
    return true;
  }

  /**
   * Stop, and resolve with the bytes.
   *
   * MediaRecorder.stop() is asynchronous by specification: it queues the final
   * `dataavailable` and then `stop`, so the blob does not exist at the moment
   * the call returns. Hence a promise, and hence the fact that any caller who
   * cannot await one — a synchronous unload handler — cannot use this and has
   * to take the chunks already collected instead. See salvage().
   */
  function stop() {
    return new Promise((resolve) => {
      const recorder = mediaRecorder;
      if (!recorder || !recording.value) {
        resolve(null);
        return;
      }

      const takeNo = takeId;
      const mimeType = typeOf(recorder);

      if (recorder.state === 'inactive') {
        // Already stopped underneath us. No event is coming; take what is here.
        resolve(harvest(takeNo, mimeType));
        return;
      }

      awaitingStop = true;
      recorder.addEventListener('stop', () => resolve(harvest(takeNo, mimeType)), { once: true });
      try {
        recorder.stop();
      } catch {
        // Nothing will fire now. Do not leave the caller waiting on a promise
        // that can never settle — a hung await here is a lost recording.
        resolve(harvest(takeNo, mimeType));
      }
    });
  }

  onBeforeUnmount(() => {
    /* The safety net, not the plan. The page's own route guard should have
     * stopped and saved before Vue ever got here; this catches the paths that
     * do not run one — a parent tearing the tree down, a hot reload, a route
     * change nobody thought about.
     *
     * 'async' is correct even though this handler itself is synchronous: an
     * unmount is a component going away inside a page that is still very much
     * alive, so the recorder's final `dataavailable` and the caller's write to
     * IndexedDB both get to run. What they run against is a closure, not a
     * mounted component, which is why the take is handed to onSalvage rather
     * than rendered — and why the page must not put anything in that callback
     * that needs the DOM to still be there.
     */
    salvage('async', 'unmounted');
    detachTeardownWatch();
  });

  return { recording, elapsedSeconds, supported, micRefused, error, start, stop };
}
